<template>
    <div>
        <!-- Read-only (embed): a Booked legend so external visitors understand
             the dot, placed top-right above the calendar to match the live
             property page's "Availability" key. -->
        <p
            v-if="readonly"
            class="mb-3 flex items-center justify-end gap-1.5 text-xsss sm:text-xs leading-none"
        >
            <span class="size-3 rounded-full bg-amber-500 -mt-1 relative"></span>
            Booked
        </p>

        <!-- Embed runs tighter vertical padding to shave height (the owner iframe
             has a fixed height, so less content means less chance of a scrollbar). -->
        <div
            ref="calendarShell"
            class="border border-brand-2-950/10 rounded-xl px-1.5 sm:px-3 xl:px-7"
            :class="readonly ? 'py-3 sm:py-4 xl:py-6' : 'py-3 sm:py-5 xl:py-10'"
        >
            <!-- Holds roughly a one-month calendar's height so the page doesn't
                 jump when the real thing mounts on approach. -->
            <div
                v-if="!calendarReady"
                class="min-h-[280px] sm:min-h-[320px]"
            ></div>
            <Datepicker
                v-if="calendarReady"
                mode="range"
                :inline="true"
                :availableDates="dates"
                :displayOnly="readonly"
                :rangeHighlight="!readonly"
                :rangeState="readonly ? '' : rangeState"
                :maxDate="maxDate()"
                :startDate="readonly ? '' : enquiryDates.from"
                :endDate="readonly ? '' : enquiryDates.to"
                :defaultDate="readonly ? [] : selection"
                @update:range="onRange"
            />
        </div>

        <!-- Non-blocking mobile confirmation near the calendar (interactive only).
             Desktop surfaces the same selection as the panel summary instead, so
             this is lg:hidden. No scroll, no modal — the user keeps browsing. -->
        <Transition>
            <p
                v-if="toast && !readonly"
                class="lg:hidden mt-3 inline-flex items-center gap-2 rounded-full bg-brand-2-950 text-white text-xssss font-semibold px-4 py-2"
                role="status"
            >
                <!-- Dot follows rangeState, the same signal that colours the
                     selection in the calendar itself: a green dot over a range
                     the calendar has just drawn as booked contradicts it. -->
                <span
                    class="size-1.5 rounded-full"
                    :class="rangeState === 'unavailable' ? 'bg-amber-400' : 'bg-emerald-400'"
                ></span>
                Dates added: {{ toast }}
            </p>
        </Transition>
    </div>
</template>

<script setup>
    import { ref, computed, defineAsyncComponent, onMounted, useTemplateRef } from 'vue';

    // Async on purpose: this pulls in flatpickr, and on the property page the
    // calendar sits well below the fold. The chunk loads when the section is
    // approaching the viewport (observer below); the embed loads it at mount.
    const Datepicker = defineAsyncComponent(() => import('@js/src/components/Datepicker.vue'));
    import {
        enquiryDates,
        setEnquiryDates,
        formatShortRange,
        rangeAvailability,
    } from '@js/src/stores/enquiryDates';

    const props = defineProps({
        dates: {
            type: Array,
            default: () => [],
        },
        // Embed use: a non-interactive availability display (no click-to-select,
        // no range highlight, no toast) with a Booked legend.
        readonly: {
            type: Boolean,
            default: false,
        },
    });

    const toast = ref('');
    let toastTimer = null;

    // The embed IS the calendar, so it renders immediately. On the property
    // page nothing mounts (and no flatpickr downloads) until the section is
    // within 600px of the viewport - loaded before the visitor reaches it, so
    // the placeholder below is rarely seen.
    const calendarShell = useTemplateRef('calendarShell');
    const calendarReady = ref(props.readonly);

    onMounted(() => {
        if (calendarReady.value) {
            return;
        }

        if (!('IntersectionObserver' in window) || !calendarShell.value) {
            calendarReady.value = true;

            return;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                calendarReady.value = true;
                observer.disconnect();
            }
        }, { rootMargin: '600px 0px' });

        observer.observe(calendarShell.value);
    });

    // Initial highlighted range fed to flatpickr at mount (from cookie prefill).
    // Live external updates are handled by Datepicker's own start/end watch.
    const selection = computed(() => (
        enquiryDates.from && enquiryDates.to ? [enquiryDates.from, enquiryDates.to] : []
    ));

    // Drives the green (available) vs amber (unavailable) selection colouring.
    const rangeState = computed(
        () => rangeAvailability(enquiryDates.from, enquiryDates.to, props.dates)
    );

    function onRange({ startDate, endDate }) {
        if (props.readonly) {
            return;
        }

        setEnquiryDates(startDate, endDate);

        // Show the confirmation using the post-swap store values so it always
        // reads check-in → check-out order.
        toast.value = formatShortRange(enquiryDates.from, enquiryDates.to);

        if (toastTimer) {
            clearTimeout(toastTimer);
        }

        toastTimer = setTimeout(() => {
            toast.value = '';
        }, 4000);
    }

    function maxDate() {
        const date = new Date();
        date.setDate(date.getDate() + 1095);

        return date.toISOString().split('T')[0];
    }
</script>
